import { useMemo, useRef, useState } from 'react'
import { Box, VStack, HStack, Switch, Text } from '@chakra-ui/react';
import {
  DndContext,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ScriptElementType } from '../../types/script'
import type {
  ScriptElementFormState,
  ScriptLane,
  TagPickerDto,
  QuestionPickerDto,
} from '../../types/script'
import { MainFlowCard } from './MainFlowCard'
import { BranchSection } from './BranchSection'
import { BranchWireOverlay } from './BranchWireOverlay'
import { getNextLaneColor, createNewElement } from './laneUtils'
import { uuid } from '../../lib/uuid'

interface VerticalFlowEditorProps {
  lanes: ScriptLane[]
  onLanesChange: (lanes: ScriptLane[]) => void
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
}

export function VerticalFlowEditor({
  lanes,
  onLanesChange,
  tagOptions,
  questionOptions,
}: VerticalFlowEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [showWires, setShowWires] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const mainLane = lanes.find((l) => l.id === 'main')
  if (!mainLane) return null

  // Memoize sortable items to prevent SortableContext from resetting on every render
  const sortableItems = useMemo(
    () => mainLane.elements.map((el) => el.clientId),
    [mainLane.elements]
  )

  // Memoize branch lookup map to prevent recreating branches array on every render
  const branchesBySourceId = useMemo(() => {
    const map = new Map<string, ScriptLane[]>()
    for (const lane of lanes) {
      if (lane.id === 'main' || !lane.sourceElementClientId) continue
      const existing = map.get(lane.sourceElementClientId) || []
      map.set(lane.sourceElementClientId, [...existing, lane])
    }
    return map
  }, [lanes])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = active.id as string
    const overId = over.id as string

    // Find which lanes the active and over items belong to
    const activeLane = lanes.find((l) =>
      l.elements.some((el) => el.clientId === activeId)
    )
    const overLane = lanes.find((l) =>
      l.elements.some((el) => el.clientId === overId)
    )

    if (!activeLane || !overLane) return

    if (activeLane.id === overLane.id) {
      // Same lane: reorder
      const oldIndex = activeLane.elements.findIndex(
        (el) => el.clientId === activeId
      )
      const newIndex = activeLane.elements.findIndex(
        (el) => el.clientId === overId
      )

      const newLanes = lanes.map((lane) => {
        if (lane.id !== activeLane.id) return lane
        return {
          ...lane,
          elements: arrayMove(lane.elements, oldIndex, newIndex).map(
            (el, idx) => ({ ...el, displayOrder: idx })
          ),
        }
      })
      onLanesChange(newLanes)
    } else {
      // Different lanes: move element between lanes
      const element = activeLane.elements.find(
        (el) => el.clientId === activeId
      )
      if (!element) return

      const overIndex = overLane.elements.findIndex(
        (el) => el.clientId === overId
      )

      const newLanes = lanes.map((lane) => {
        if (lane.id === activeLane.id) {
          return {
            ...lane,
            elements: lane.elements
              .filter((el) => el.clientId !== activeId)
              .map((el, idx) => ({ ...el, displayOrder: idx })),
          }
        }
        if (lane.id === overLane.id) {
          const newElements = [...lane.elements]
          newElements.splice(overIndex, 0, element)
          return {
            ...lane,
            elements: newElements.map((el, idx) => ({
              ...el,
              displayOrder: idx,
            })),
          }
        }
        return lane
      })
      onLanesChange(newLanes)
    }
  }

  const handleAddMainElement = (type: ScriptElementType) => {
    handleAddElement('main', type)
  }

  const handleAddElement = (laneId: string, type: ScriptElementType) => {
    const newElement = createNewElement(type)
    const newLanes = lanes.map((lane) => {
      if (lane.id !== laneId) return lane
      const elements = [...lane.elements, newElement].map((el, idx) => ({
        ...el,
        displayOrder: idx,
      }))
      return { ...lane, elements }
    })
    onLanesChange(newLanes)
  }

  const handleUpdateElement = (
    laneId: string,
    clientId: string,
    updates: Partial<ScriptElementFormState>
  ) => {
    const newLanes = lanes.map((lane) => {
      if (lane.id !== laneId) return lane
      return {
        ...lane,
        elements: lane.elements.map((el) =>
          el.clientId === clientId ? { ...el, ...updates } : el
        ),
      }
    })
    onLanesChange(newLanes)
  }

  const handleRemoveElement = (laneId: string, clientId: string) => {
    const newLanes = lanes.map((lane) => {
      if (lane.id !== laneId) return lane
      return {
        ...lane,
        elements: lane.elements
          .filter((el) => el.clientId !== clientId)
          .map((el, idx) => ({ ...el, displayOrder: idx })),
      }
    })
    onLanesChange(newLanes)
  }

  const handleCreateBranch = (
    sourceClientId: string,
    branchType: string,
    branchKey: string,
    label: string
  ) => {
    // Check if a lane already exists for this branch
    const existing = lanes.find(
      (l) =>
        l.sourceElementClientId === sourceClientId &&
        l.sourceBranchKey === branchKey
    )
    if (existing) return // Lane already exists

    const newLane: ScriptLane = {
      id: uuid(),
      label,
      colorScheme: getNextLaneColor(lanes),
      elements: [],
      sourceElementClientId: sourceClientId,
      sourceBranchType: branchType as ScriptLane['sourceBranchType'],
      sourceBranchKey: branchKey,
      mergeTargetClientId: undefined,
    }

    // Find the source element's lane and insert the new lane right after it
    const sourceLaneIdx = lanes.findIndex((l) =>
      l.elements.some((el) => el.clientId === sourceClientId)
    )
    const newLanes = [...lanes]
    newLanes.splice(sourceLaneIdx + 1, 0, newLane)
    onLanesChange(newLanes)
  }

  const handleDeleteLane = (laneId: string) => {
    const lane = lanes.find((l) => l.id === laneId)
    if (!lane || lane.id === 'main') return

    // Collect element IDs being deleted (to clean up merge targets pointing here)
    const deletedElementIds = new Set(lane.elements.map((el) => el.clientId))

    const newLanes = lanes
      .filter((l) => l.id !== laneId)
      .map((l) => ({
        ...l,
        // Clear merge targets that point to elements in the deleted lane
        mergeTargetClientId:
          l.mergeTargetClientId && deletedElementIds.has(l.mergeTargetClientId)
            ? undefined
            : l.mergeTargetClientId,
        elements: l.elements.map((el) => {
          if (el.clientId !== lane.sourceElementClientId) return el
          // Clean up branch pointers on the source element
          const updates: Partial<ScriptElementFormState> = {}
          if (lane.sourceBranchType === 'true') {
            updates.nextElementOnTrueClientId = null
          } else if (lane.sourceBranchType === 'false') {
            updates.nextElementOnFalseClientId = null
          } else if (lane.sourceBranchType === 'answer' && lane.sourceBranchKey) {
            const ab = { ...(el.answerBranches || {}) }
            delete ab[lane.sourceBranchKey]
            updates.answerBranches = ab
          } else if (lane.sourceBranchType === 'prompt-response' && lane.sourceBranchKey) {
            updates.promptResponses = (el.promptResponses || []).map((pr) =>
              pr.label === lane.sourceBranchKey
                ? { ...pr, targetClientId: null }
                : pr
            )
          }
          return { ...el, ...updates }
        }),
      }))
    onLanesChange(newLanes)
  }

  const handleSetMergeTarget = (laneId: string, targetClientId: string | undefined) => {
    const newLanes = lanes.map((lane) => {
      if (lane.id !== laneId) return lane
      return { ...lane, mergeTargetClientId: targetClientId }
    })
    onLanesChange(newLanes)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableItems}
        strategy={verticalListSortingStrategy}
      >
        <Box position="relative" maxW="900px" mx="auto" ref={containerRef}>
          <HStack
            display={{ base: 'none', md: 'flex' }}
            justify="flex-end"
            gap={2}
            mb={2}
          >
            <Text fontSize="xs" color="gray.500">
              Show flow wires
            </Text>
            <Switch.Root
              size="sm"
              checked={showWires}
              onCheckedChange={(e) => setShowWires(e.checked === true)}
              aria-label="Show flow wires"
            >
              <Switch.HiddenInput />
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
          </HStack>

          <VStack gap={6} align="stretch" pr={{ base: 0, md: '74px' }}>
          {/* Render main flow with inline branch sections */}
          {mainLane.elements.length === 0 && (
            <Box py={8} textAlign="center">
              <MainFlowCard
                element={null}
                lanes={lanes}
                stepNumber={null}
                onUpdate={() => {}}
                onRemove={() => {}}
                onCreateBranch={() => {}}
                onAddElement={handleAddMainElement}
                tagOptions={tagOptions}
                questionOptions={questionOptions}
              />
            </Box>
          )}

          {mainLane.elements.map((element, idx) => {
            const branches = branchesBySourceId.get(element.clientId) || []

            return (
              <Box key={element.clientId}>
                {/* Main flow element */}
                <MainFlowCard
                  element={element}
                  lanes={lanes}
                  stepNumber={idx + 1}
                  onUpdate={(updates) =>
                    handleUpdateElement('main', element.clientId, updates)
                  }
                  onRemove={() => handleRemoveElement('main', element.clientId)}
                  onCreateBranch={(branchType, branchKey, label) =>
                    handleCreateBranch(element.clientId, branchType, branchKey, label)
                  }
                  onAddElement={handleAddMainElement}
                  tagOptions={tagOptions}
                  questionOptions={questionOptions}
                />

                {/* Branch section (if this element has branches) */}
                {branches.length > 0 && (
                  <BranchSection
                    branches={branches}
                    lanes={lanes}
                    onAddElement={handleAddElement}
                    onUpdateElement={handleUpdateElement}
                    onRemoveElement={handleRemoveElement}
                    onCreateBranch={handleCreateBranch}
                    onDeleteLane={handleDeleteLane}
                    onSetMergeTarget={handleSetMergeTarget}
                    tagOptions={tagOptions}
                    questionOptions={questionOptions}
                  />
                )}
              </Box>
            )
          })}

          {/* Add element button at the end of main flow */}
          {mainLane.elements.length > 0 && (
            <Box py={4}>
              <MainFlowCard
                element={null}
                lanes={lanes}
                stepNumber={null}
                onUpdate={() => {}}
                onRemove={() => {}}
                onCreateBranch={() => {}}
                onAddElement={handleAddMainElement}
                tagOptions={tagOptions}
                questionOptions={questionOptions}
              />
            </Box>
          )}
          </VStack>

          <BranchWireOverlay lanes={lanes} containerRef={containerRef} enabled={showWires} />
        </Box>
      </SortableContext>
    </DndContext>
  );
}
